import { init } from "gg-wf-scripts";
import { createClient } from "@supabase/supabase-js";
import type { Ctx } from "./types";

// queries — jobs
import { job_by_id } from "./queries/jobs/job_by_id";
import { jobs_list } from "./queries/jobs/jobs_list";
import { current_drafts } from "./queries/jobs/current_drafts";
import { unclaimed_jobs } from "./queries/jobs/unclaimed_jobs";
import { open_jobs } from "./queries/jobs/open_jobs";
import {
  my_jobs,
  my_upcoming_jobs,
  my_past_jobs,
} from "./queries/jobs/my_jobs";
import { jobs_ready_to_review } from "./queries/jobs/jobs_ready_to_review";
import { jobs_approved } from "./queries/jobs/jobs_approved";
import { jobs_approved_unpaid } from "./queries/jobs/jobs_approved_unpaid";
import { jobs_paid } from "./queries/jobs/jobs_paid";
import { jobs_rejected } from "./queries/jobs/jobs_rejected";
import { recent_job_activity } from "./queries/jobs/recent_job_activity";

// queries — schools
import { school_by_id } from "./queries/schools/school_by_id";
import { schools_list } from "./queries/schools/schools_list";
import { my_schools } from "./queries/schools/my_schools";
import { recent_school_activity } from "./queries/schools/recent_school_activity";
import { school_engagement } from "./queries/schools/school_engagement";

// queries — users
import { user_by_id } from "./queries/users/user_by_id";
import { user_self } from "./queries/users/user_self";
import { users_list } from "./queries/users/users_list";
import { recent_user_activity } from "./queries/users/recent_user_activity";
import { pending_invites } from "./queries/users/pending_invites";
import { sub_engagement } from "./queries/users/sub_engagement";

// queries — stats
import { superuser_stats } from "./queries/stats/superuser_stats";
import { school_admin_stats } from "./queries/stats/school_admin_stats";
import { substitute_stats } from "./queries/stats/substitute_stats";

// queries — selects
import { select_substitutes } from "./queries/selects/select_substitutes";
import { select_school_admins } from "./queries/selects/select_school_admins";
import { select_superusers } from "./queries/selects/select_superusers";
import { select_schools } from "./queries/selects/select_schools";
import { select_grades } from "./queries/selects/select_grades";
import { select_subjects } from "./queries/selects/select_subjects";
import { select_job_statuses } from "./queries/selects/select_job_statuses";
import { select_review_statuses } from "./queries/selects/select_review_statuses";
import { select_pay_statuses } from "./queries/selects/select_pay_statuses";

// actions
import { sign_out } from "./actions/sign_out";
import { toggle_table_sort } from "./actions/toggle_table_sort";
import { test_toast } from "./actions/test_toast";
import { delete_school } from "./actions/schools/delete_school";
import { delete_job_opening } from "./actions/jobs/delete_job_opening";
import { claim_job } from "./actions/jobs/claim_job";
import { update_job_status } from "./actions/jobs/update_job_status";
import { update_review_status } from "./actions/jobs/update_review_status";
import { update_pay_status } from "./actions/jobs/update_pay_status";
import { job_set_draft } from "./actions/jobs/job_set_draft";
import { job_set_open } from "./actions/jobs/job_set_open";
import { job_set_cancelled } from "./actions/jobs/job_set_cancelled";
import { job_set_ready_to_review } from "./actions/jobs/job_set_ready_to_review";
import { job_set_approved } from "./actions/jobs/job_set_approved";
import { job_set_rejected } from "./actions/jobs/job_set_rejected";
import { job_set_paid } from "./actions/jobs/job_set_paid";
import { job_set_unpaid } from "./actions/jobs/job_set_unpaid";
import { export_jobs_approved_unpaid_csv } from "./actions/jobs/export_jobs_approved_unpaid_csv";
import { mark_approved_unpaid_as_paid } from "./actions/jobs/mark_approved_unpaid_as_paid";
import { delete_user } from "./actions/users/delete_user";
import { resend_invite } from "./actions/users/resend_invite";
import { cancel_invite } from "./actions/users/cancel_invite";
import { set_notification_preference } from "./actions/users/set_notification_preference";
import { increment_bonus_count } from "./actions/users/increment_bonus_count";
import { decrement_bonus_count } from "./actions/users/decrement_bonus_count";

// form actions
import { sign_in } from "./formActions/auth/sign_in";
import { set_password } from "./formActions/auth/set_password";
import { request_password_reset } from "./formActions/auth/request_password_reset";
import { invite_user } from "./formActions/users/invite_user";
import { update_profile } from "./formActions/users/update_profile";
import { update_self } from "./formActions/users/update_self";
import { create_school } from "./formActions/schools/create_school";
import { update_school } from "./formActions/schools/update_school";
import { create_job_opening } from "./formActions/jobs/create_job_opening";
import { update_job_opening } from "./formActions/jobs/update_job_opening";

const sb = createClient(
  "https://demplabxtiijdsehqhvt.supabase.co",
  "sb_publishable_zIODF9VTLjAZxXtLCqGgpw_H-G2TdDb",
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  }
);

document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    sb.auth.stopAutoRefresh();
  } else {
    sb.auth.startAutoRefresh();
  }
});

const app = init<Ctx>({
  transition: {
    easing: "easeOut",
    duration: 250,
  },
  debug: window.location.href.includes("webflow.io"),
  context: { sb },
  auth: {
    getUser: async () =>
      (await sb.auth.getSession()).data.session?.user?.id ?? null,
    onChange: (cb) =>
      sb.auth.onAuthStateChange((_e, session) => cb(session?.user?.id ?? null)),
    roleQuery: async (_ctx, userId) => {
      const { data } = await sb
        .from("profiles_internal")
        .select("role")
        .eq("user_id", userId)
        .single();
      return data?.role ?? null;
    },
    signedOutRedirect: "/app/sign-in",
    unauthorizedRedirect: "/404",
  },
});

// ---- queries ----

app.addQuery("school_by_id", school_by_id, {
  on: ["id"],
  refreshKeys: ["schools"],
});
app.addQuery("job_by_id", job_by_id, {
  on: ["id"],
  refreshKeys: ["jobs"],
});
app.addQuery("user_by_id", user_by_id, {
  on: ["id"],
  refreshKeys: ["users"],
});
app.addQuery("user_self", user_self, { refreshKeys: ["users"] });
app.addQuery("superuser_stats", superuser_stats, {
  on: ["id"],
  refreshKeys: ["users", "schools", "jobs"],
});
app.addQuery("school_admin_stats", school_admin_stats, {
  on: ["id"],
  refreshKeys: ["jobs"],
});
app.addQuery("substitute_stats", substitute_stats, {
  on: ["id"],
  refreshKeys: ["jobs", "users"],
});
app.addQuery("recent_job_activity", recent_job_activity, {
  refreshKeys: ["jobs"],
});
app.addQuery("recent_school_activity", recent_school_activity, {
  refreshKeys: ["schools"],
});
app.addQuery("recent_user_activity", recent_user_activity, {
  refreshKeys: ["users"],
});
app.addQuery("pending_invites", pending_invites, {
  refreshKeys: ["users"],
});
app.addQuery("school_engagement", school_engagement, {
  refreshKeys: ["schools", "jobs"],
});
app.addQuery("sub_engagement", sub_engagement, {
  refreshKeys: ["users", "jobs"],
});
app.addQuery("jobs_list", jobs_list, {
  on: ["q", "sortBy", "sortDir"],
  refreshKeys: ["jobs"],
});
app.addQuery("current_drafts", current_drafts, {
  on: ["q", "sortBy", "sortDir"],
  refreshKeys: ["jobs"],
});
app.addQuery("unclaimed_jobs", unclaimed_jobs, {
  on: ["q", "sortBy", "sortDir"],
  refreshKeys: ["jobs"],
});
app.addQuery("open_jobs", open_jobs, {
  on: ["q", "sortBy", "sortDir"],
  refreshKeys: ["jobs"],
});
app.addQuery("users_list", users_list, {
  on: ["q", "sortBy", "sortDir"],
  refreshKeys: ["users"],
});
app.addQuery("schools_list", schools_list, {
  on: ["q", "sortBy", "sortDir"],
  refreshKeys: ["schools"],
});
app.addQuery("my_schools", my_schools, {
  on: ["sortBy", "sortDir"],
  refreshKeys: ["schools", "users"],
});
app.addQuery("my_jobs", my_jobs, {
  on: ["q", "sortBy", "sortDir"],
  refreshKeys: ["jobs"],
});
app.addQuery("my_upcoming_jobs", my_upcoming_jobs, {
  on: ["q", "sortBy", "sortDir"],
  refreshKeys: ["jobs"],
});
app.addQuery("my_past_jobs", my_past_jobs, {
  on: ["q", "sortBy", "sortDir"],
  refreshKeys: ["jobs"],
});
app.addQuery("jobs_ready_to_review", jobs_ready_to_review, {
  on: ["q", "sortBy", "sortDir", "view"],
  refreshKeys: ["jobs"],
});
app.addQuery("jobs_approved", jobs_approved, {
  on: ["q", "sortBy", "sortDir", "view"],
  refreshKeys: ["jobs"],
});
app.addQuery("jobs_approved_unpaid", jobs_approved_unpaid, {
  on: ["q", "sortBy", "sortDir", "view"],
  refreshKeys: ["jobs"],
});
app.addQuery("jobs_paid", jobs_paid, {
  on: ["q", "sortBy", "sortDir", "view"],
  refreshKeys: ["jobs"],
});
app.addQuery("jobs_rejected", jobs_rejected, {
  on: ["q", "sortBy", "sortDir", "view"],
  refreshKeys: ["jobs"],
});

// ---- select queries ----

app.addQuery("select_substitutes", select_substitutes, {
  refreshKeys: ["users"],
});
app.addQuery("select_school_admins", select_school_admins, {
  refreshKeys: ["users"],
});
app.addQuery("select_superusers", select_superusers, {
  refreshKeys: ["users"],
});
app.addQuery("select_schools", select_schools, { refreshKeys: ["schools"] });
app.addQuery("select_grades", select_grades);
app.addQuery("select_subjects", select_subjects);
app.addQuery("select_job_statuses", select_job_statuses);
app.addQuery("select_review_statuses", select_review_statuses);
app.addQuery("select_pay_statuses", select_pay_statuses);

// ---- actions ----

app.addAction("sign_out", sign_out);
app.addAction("toggle_table_sort", toggle_table_sort);
app.addAction("test_toast", test_toast);
app.addAction("delete_school", delete_school);
app.addAction("delete_job_opening", delete_job_opening);
app.addAction("claim_job", claim_job);
app.addAction("update_job_status", update_job_status);
app.addAction("update_review_status", update_review_status);
app.addAction("update_pay_status", update_pay_status);
app.addAction("job_set_draft", job_set_draft);
app.addAction("job_set_open", job_set_open);
app.addAction("job_set_cancelled", job_set_cancelled);
app.addAction("job_set_ready_to_review", job_set_ready_to_review);
app.addAction("job_set_approved", job_set_approved);
app.addAction("job_set_rejected", job_set_rejected);
app.addAction("job_set_paid", job_set_paid);
app.addAction("job_set_unpaid", job_set_unpaid);
app.addAction(
  "export_jobs_approved_unpaid_csv",
  export_jobs_approved_unpaid_csv
);
app.addAction("mark_approved_unpaid_as_paid", mark_approved_unpaid_as_paid);
app.addAction("delete_user", delete_user);
app.addAction("resend_invite", resend_invite);
app.addAction("cancel_invite", cancel_invite);
app.addAction("set_notification_preference", set_notification_preference);
app.addAction("increment_bonus_count", increment_bonus_count);
app.addAction("decrement_bonus_count", decrement_bonus_count);

// ---- form actions ----

app.addFormAction("sign_in", sign_in);
app.addFormAction("set_password", set_password);
app.addFormAction("request_password_reset", request_password_reset);
app.addFormAction("invite_user", invite_user);
app.addFormAction("update_profile", update_profile);
app.addFormAction("update_self", update_self);
app.addFormAction("create_school", create_school);
app.addFormAction("update_school", update_school);
app.addFormAction("create_job_opening", create_job_opening);
app.addFormAction("update_job_opening", update_job_opening);

app.start();
